import React, { useState, useEffect } from 'react';
import { Steps, Card, Typography, Button, Space, Alert, Drawer, Grid } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from "@/components/common/ThemeProvider";
import { useCompany } from "@/hooks/company/use-company";
import {
    showSuccessNotification,
    showErrorNotification,
    showWarningNotification
} from '@/components/common/utils/notify';
const companyLogo = '/images/login.png';

// Step Components
import PhoneVerificationStep from '../components/steps/PhoneVerificationStep';
import TermsAcceptanceStep from '../components/steps/TermsAcceptanceStep';
import EmailVerificationStep from '../components/steps/EmailVerificationStep';
import PersonalInfoStep from '../components/steps/PersonalInfoStep';
import LocationSelectionStep from '../components/steps/LocationSelectionStep';
import DocumentUploadStep from '../components/steps/DocumentUploadStep';
import OfficerApprovalStep from '../components/steps/OfficerApprovalStep';
import DepositStep from '../components/steps/DepositStep';
import DeductionFormStep from '../components/steps/DeductionFormStep';
import RegistrationCompleteStep from '../components/steps/RegistrationCompleteStep';

// Eligibility Component
import EligibilityCheckStep, { EligibilityData } from '../components/EligibilityCheckStep';

// Types
import { SelfRegistrationSessionDTO, SelfOnboardingConfigDTO } from '@/shared/api/model';

// Hooks
import { useSelfRegistration } from '../hooks/useSelfRegistration';
import { useGetActiveConfig } from '@/modules/onboarding/hooks/useSelfOnboardingConfig';
import {useGetLoginLogo} from "@/shared/api/client/appearance-controller";
const defaultCompanyLogo = '/images/hazina-logo.jpg';

const { Step } = Steps;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Registration Steps Configuration (Phone verification is NOT included as it's a security gate)
const REGISTRATION_STEPS = [
    { key: 'TERMS_ACCEPTANCE', title: 'Terms & Conditions', description: 'Accept terms and conditions' },
    { key: 'EMAIL_VERIFICATION', title: 'Email Verification', description: 'Verify your email address' },
    { key: 'ELIGIBILITY_CHECK', title: 'Eligibility Check', description: 'Confirm eligibility requirements' },
    { key: 'PERSONAL_INFO', title: 'Personal Information', description: 'Provide personal details' },
    { key: 'LOCATION_SELECTION', title: 'Location Information', description: 'Provide address details' },
    { key: 'DOCUMENT_UPLOAD', title: 'Document Upload', description: 'Upload required documents' },
    { key: 'OFFICER_REVIEW', title: 'Officer Review', description: 'Wait for officer approval' },
    { key: 'DEPOSIT', title: 'Initial Deposit', description: 'Make your initial deposit' },
    { key: 'DEDUCTION_FORM', title: 'Deduction Form', description: 'Download, sign and upload deduction form' },
    { key: 'REGISTRATION_COMPLETE', title: 'Registration Complete', description: 'Registration successful' }
];

const MultiStepSelfRegistrationView: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { dark } = useTheme();
    const { data: company } = useCompany();
    const screens = useBreakpoint();
    
    // Mobile navigation state
    const [mobileStepsVisible, setMobileStepsVisible] = useState(false);
    const isMobile = !screens.md;

    // Get active self-onboarding config
    const { data: activeConfigResponse, isLoading: isLoadingConfig } = useGetActiveConfig();
    const activeConfig = activeConfigResponse as SelfOnboardingConfigDTO;

    // Get session token from URL params if resuming
    const sessionToken = searchParams.get('session');
    console.log('🔍 Session token from URL:', sessionToken);

    // Self Registration Hook - use session query directly
    const { getSession, getCurrentSession, exitRegistration, updatePersonalInfo } = useSelfRegistration();
    const sessionQuery = getSession(sessionToken);
    console.log('🔍 Session query:', sessionQuery);

    // State management
    const [currentStep, setCurrentStep] = useState(0);
    const [session, setSession] = useState<SelfRegistrationSessionDTO | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isPhoneVerificationMode, setIsPhoneVerificationMode] = useState(false);

    const { data: logoData, isLoading: logoLoading } = useGetLoginLogo();
    const logoSrc = typeof logoData === 'string' ? logoData : logoData?.data || defaultCompanyLogo;

    // Update session and step when session data is loaded (only on initial load via URL session token)
    useEffect(() => {
        console.log('🔍 useEffect triggered - sessionQuery.data:', sessionQuery.data);

        if (sessionQuery.data) {
            const sessionData = sessionQuery.data;
            console.log('✅ Session data loaded:', sessionData);

            const actualSessionData = sessionData;

            // Only update session state - don't override currentStep if user has progressed further
            setSession(prev => {
                // If we already have a session and have progressed, keep it unless backend says we're further
                if (prev) {
                    const backendStepIndex = getStepIndex(actualSessionData.currentStep);
                    if (backendStepIndex >= 0 && backendStepIndex > currentStep) {
                        setCurrentStep(backendStepIndex);
                    }
                    return actualSessionData;
                }
                // First load: use backend step
                if (!actualSessionData.phoneVerified && !isPhoneVerificationMode) {
                    console.log('🔒 Session loaded but phone not verified - will show phone verification gate');
                    setIsPhoneVerificationMode(true);
                } else if (actualSessionData.phoneVerified) {
                    console.log('✅ Session loaded with verified phone - navigating to step:', actualSessionData.currentStep);
                    const stepIndex = getStepIndex(actualSessionData.currentStep);
                    setCurrentStep(stepIndex >= 0 ? stepIndex : 0);
                }
                return actualSessionData;
            });
        } else if (sessionQuery.error && sessionToken) {
            console.error('❌ Session loading error:', sessionQuery.error);
            setError('Failed to load registration session');
            showErrorNotification('Failed to load registration session');
        } else if (!sessionToken) {
            console.log('⚠️ No session token provided - user needs to start registration');
        }
    }, [sessionQuery.data, sessionQuery.error, sessionToken]);

    useEffect(() => {
        if (sessionToken || session) {
            return;
        }

        let cancelled = false;
        getCurrentSession()
            .then((currentSession) => {
                if (!cancelled && currentSession) {
                    setSession(currentSession);
                    setIsPhoneVerificationMode(!currentSession.phoneVerified);
                    const stepIndex = getStepIndex(currentSession.currentStep);
                    setCurrentStep(stepIndex >= 0 ? stepIndex : 0);
                }
            })
            .catch((currentSessionError) => {
                if (!cancelled) {
                    console.error('Failed to recover registration session from cookie:', currentSessionError);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [sessionToken, session]);

    const getStepIndex = (stepKey: string): number => {
        // DEDUCTION_FORM_REVIEW is handled within DEDUCTION_FORM step
        if (stepKey === 'DEDUCTION_FORM_REVIEW') {
            stepKey = 'DEDUCTION_FORM';
        }
        return REGISTRATION_STEPS.findIndex(step => step.key === stepKey);
    };

    const handleStepComplete = (sessionData: SelfRegistrationSessionDTO) => {
        // Clear any existing errors since step completed successfully
        setError(null);

        const actualSessionData = sessionData;
        setSession(actualSessionData);

        // Backend now controls all step progression - just trust the currentStep from backend
        const nextStepIndex = getStepIndex(actualSessionData.currentStep);
        console.log('🔄 Backend determined next step index:', nextStepIndex);

        // If phone verification was just completed, exit phone verification mode
        if (isPhoneVerificationMode && actualSessionData.phoneVerified) {
            console.log('🔓 Exiting phone verification mode');
            setIsPhoneVerificationMode(false);
        }

        if (nextStepIndex >= 0) {
            console.log('🔄 Setting current step to:', nextStepIndex);
            setCurrentStep(nextStepIndex);
        } else {
            console.warn('⚠️ Could not find step index for backend step:', actualSessionData.currentStep);
            console.log('Available steps:', REGISTRATION_STEPS.map(s => s.key));
        }
    };

    const handleError = (message: string) => {
        setError(message);
        showErrorNotification(message);
    };

    // Clear error when user manually closes it or when step changes
    const clearError = () => {
        setError(null);
    };

    // Clear error when current step changes (user progressed)
    React.useEffect(() => {
        setError(null);
    }, [currentStep]);

    const renderCurrentStep = () => {
        // Show phone verification if session exists but phone is not verified
        if (session && !session.phoneVerified) {
            console.log('🔒 Showing phone verification gate - phone not verified');
            // Set phone verification mode when showing phone verification for existing session
            if (!isPhoneVerificationMode) {
                setIsPhoneVerificationMode(true);
            }
            return (
                <PhoneVerificationStep
                    session={session}
                    onComplete={handleStepComplete}
                    onError={handleError}
                />
            );
        }

        if (!session && currentStep > 0) {
            return (
                <Alert
                    message="Session Required"
                    description="Please start the registration process from the beginning."
                    type="warning"
                    showIcon
                    action={
                        <Button size="small" onClick={() => setCurrentStep(0)}>
                            Start Over
                        </Button>
                    }
                />
            );
        }

        // If no session, show phone verification to start registration
        if (!session) {
            console.log('🔒 No session - showing phone verification to start registration');
            // This is initial registration, not resume mode
            if (isPhoneVerificationMode) {
                setIsPhoneVerificationMode(false);
            }
            return (
                <PhoneVerificationStep
                    session={session}
                    onComplete={handleStepComplete}
                    onError={handleError}
                />
            );
        }

        // Regular registration steps (phone is already verified)
        switch (currentStep) {
            case 0: // TERMS_ACCEPTANCE
                return (
                    <TermsAcceptanceStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 1: // EMAIL_VERIFICATION
                return (
                    <EmailVerificationStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 2: // ELIGIBILITY_CHECK
                return (
                    <EligibilityCheckStep
                        dark={dark}
                        minimumMonthlyContribution={activeConfig?.minimumMonthlyContribution}
                        shareCapital={activeConfig?.shareCapital}
                        initialData={{
                            governmentEmployee: session?.governmentEmployee ?? false,
                            grossSalary: session?.grossSalary ? Number(session.grossSalary) : undefined,
                            netSalary: session?.netSalary ? Number(session.netSalary) : undefined,
                            monthlyDeductionAmount: session?.monthlyDeductionAmount ? Number(session.monthlyDeductionAmount) : undefined,
                            termsAccepted: session?.termsAccepted ?? false
                        }}
                        onNext={async (values: EligibilityData) => {
                            const effectiveMinimumMonthlyContribution = activeConfig?.minimumMonthlyContribution ?? 50000;
                            const grossSalary = Number(values.grossSalary);
                            const netSalary = Number(values.netSalary);
                            const monthlyDeductionAmount = Number(values.monthlyDeductionAmount);

                            if (!Number.isFinite(grossSalary) || grossSalary <= 0) {
                                showErrorNotification('Please provide a valid gross salary.');
                                return;
                            }
                            if (!Number.isFinite(netSalary) || netSalary < 0) {
                                showErrorNotification('Please provide a valid net salary.');
                                return;
                            }
                            if (!Number.isFinite(monthlyDeductionAmount) || monthlyDeductionAmount < effectiveMinimumMonthlyContribution) {
                                showErrorNotification(`Monthly deduction amount must be at least TZS ${new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}`);
                                return;
                            }

                            const remainingAfterDeduction = netSalary - monthlyDeductionAmount;
                            const ratio = remainingAfterDeduction / grossSalary;
                            const meetsOneThirdRule = ratio >= (1 / 3);

                            if (!meetsOneThirdRule) {
                                showWarningNotification('You do not meet the one-third rule. Registration cannot proceed.');
                                return;
                            }

                            if (!session?.sessionToken) {
                                showErrorNotification('Session token is missing. Please restart registration.');
                                return;
                            }

                            // If eligibility already checked, skip API call and advance directly
                            if (session?.eligibilityChecked) {
                                setCurrentStep(3); // PERSONAL_INFO
                                return;
                            }

                            try {
                                const updatedSession = await updatePersonalInfo({
                                    sessionToken: session.sessionToken,
                                    governmentEmployee: values.governmentEmployee,
                                    grossSalary: String(values.grossSalary),
                                    netSalary: String(values.netSalary),
                                    monthlyDeductionAmount: String(values.monthlyDeductionAmount)
                                } as any);

                                handleStepComplete(updatedSession);
                            } catch (e: any) {
                                showErrorNotification(e?.message || 'Failed to save eligibility details');
                            }
                        }}
                    />
                );
            case 3: // PERSONAL_INFO
                return (
                    <PersonalInfoStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 4: // LOCATION_SELECTION
                return (
                    <LocationSelectionStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 5: // DOCUMENT_UPLOAD
                return (
                    <DocumentUploadStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 6: // OFFICER_APPROVAL
                return (
                    <OfficerApprovalStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 7: // DEPOSIT
                return (
                    <DepositStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                        config={activeConfig}
                    />
                );
            case 8: // DEDUCTION_FORM
                return (
                    <DeductionFormStep
                        session={session!}
                        onComplete={handleStepComplete}
                        onError={handleError}
                    />
                );
            case 9: // COMPLETED
                return (
                    <RegistrationCompleteStep
                        session={session!}
                        onComplete={() => navigate('/login')}
                        onError={handleError}
                    />
                );
            default:
                return <div>Invalid step</div>;
        }
    };

    const getStepStatus = (stepIndex: number) => {
        if (stepIndex < currentStep) return 'finish';
        if (stepIndex === currentStep) return 'process';
        return 'wait';
    };

    const handleExit = async () => {
        if (!window.confirm('Exit this registration? Your saved progress will remain available when you start again with the same phone number.')) {
            return;
        }

        try {
            await exitRegistration();
            setSession(null);
            setCurrentStep(0);
            setIsPhoneVerificationMode(false);
            setError(null);
            navigate('/register', { replace: true });
            showSuccessNotification('Registration exited. Your progress has been saved.');
        } catch (exitError) {
            console.error('Failed to exit registration:', exitError);
            showErrorNotification('Failed to exit registration');
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: dark
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(to right, #6b7280, #6b7280, #4b5563)',
            padding: isMobile ? '10px' : '20px',
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'center'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: '1000px',
                    borderRadius: isMobile ? '8px' : '12px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    margin: isMobile ? '10px' : '20px'
                }}
                bodyStyle={{ padding: isMobile ? '20px' : '40px' }}
            >
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                    {company?.logo ? (
                        <img
                            src={logoSrc}
                            alt="Company Logo"
                            style={{ height: '60px', marginBottom: '20px' }}
                        />
                    ) : (
                        <img
                            src={logoSrc}
                            alt="ScaleFi Logo"
                            style={{ height: '60px', marginBottom: '20px' }}
                        />
                    )}
                    <Title level={isMobile ? 3 : 2} style={{ margin: 0, color: '#1890ff' }}>
                        Self Registration
                    </Title>
                    <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                        Complete your registration in simple steps
                    </Text>
                </div>

                {/* Mobile Steps Button */}
                {isMobile && (
                    <div style={{ marginBottom: '20px' }}>
                        <Button 
                            icon={<MenuOutlined />} 
                            onClick={() => setMobileStepsVisible(true)}
                            style={{ width: '100%' }}
                        >
                            View Progress ({currentStep + 1}/{REGISTRATION_STEPS.length})
                        </Button>
                    </div>
                )}

                {/* Main Content Area with Steps and Form */}
                <div style={{ 
                    display: 'flex', 
                    gap: isMobile ? '0' : '40px', 
                    alignItems: 'flex-start',
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    {/* Progress Steps - Desktop Side Panel */}
                    {!isMobile && (
                        <div style={{ flex: '0 0 300px' }}>
                            <Steps
                                current={currentStep}
                                direction="vertical"
                                size="small"
                            >
                                {REGISTRATION_STEPS.map((step, index) => (
                                    <Step
                                        key={step.key}
                                        title={step.title}
                                        description={step.description}
                                        status={getStepStatus(index)}
                                    />
                                ))}
                            </Steps>
                        </div>
                    )}

                    {/* Mobile Steps Drawer */}
                    <Drawer
                        title="Registration Progress"
                        placement="left"
                        onClose={() => setMobileStepsVisible(false)}
                        open={mobileStepsVisible}
                        width={280}
                    >
                        <Steps
                            current={currentStep}
                            direction="vertical"
                            size="small"
                        >
                            {REGISTRATION_STEPS.map((step, index) => (
                                <Step
                                    key={step.key}
                                    title={step.title}
                                    description={step.description}
                                    status={getStepStatus(index)}
                                />
                            ))}
                        </Steps>
                    </Drawer>

                    {/* Content Area */}
                    <div style={{ flex: 1, width: '100%' }}>
                        {/* Error Display */}
                        {error && (
                            <Alert
                                message="Error"
                                description={error}
                                type="error"
                                showIcon
                                closable
                                onClose={clearError}
                                style={{ marginBottom: '20px' }}
                            />
                        )}

                        {/* Current Step Content */}
                        <div style={{ minHeight: isMobile ? '300px' : '400px' }}>
                            {isLoadingConfig ? <div>Loading configuration...</div> : renderCurrentStep()}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    textAlign: 'center',
                    marginTop: isMobile ? '20px' : '30px',
                    paddingTop: isMobile ? '15px' : '20px',
                    borderTop: '1px solid #f0f0f0'
                }}>
                    <Space direction={isMobile ? 'vertical' : 'horizontal'} size={isMobile ? 'small' : 'middle'}>
                        <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                            Already have an account?
                        </Text>
                        {session && (
                            <Button danger type="link" onClick={handleExit} size={isMobile ? 'small' : 'middle'}>
                                Exit Registration
                            </Button>
                        )}
                        <Button type="link" onClick={() => navigate('/login')} size={isMobile ? 'small' : 'middle'}>
                            Sign In
                        </Button>
                    </Space>
                </div>
            </Card>
        </div>
    );
};

export default MultiStepSelfRegistrationView;
